import { useState } from 'react'
import { AddPositionForm } from './AddPositionForm'
import { EditPositionForm } from './EditPositionForm'
import { EarningsBadge } from '@/components/shared/EarningsBadge'
import { aggregatePositionsByTicker, type AggregatedPosition } from '@/lib/positionAggregation'
import { useColumnOrder } from '@/hooks/useColumnOrder'
import type { Position } from '@/types'

/** Format a date string (YYYY-MM-DD) as MM/DD/YYYY. Returns — for empty/invalid. */
function formatMDY(val: string | null | undefined): string {
  const str = String(val ?? '')
  if (!str || str === '—') return '—'
  const parts = str.split('-')
  if (parts.length !== 3) return str
  const [year, month, day] = parts
  return `${month}/${day}/${year}`
}

/** The date a trade was completed, used for chronological ordering.
 *  Prefers closeDate (actual completion), falls back to expDate, then buyDate. */
function completionDate(t: any): string {
  return t.closeDate || t.expDate || t.buyDate || ''
}

/**
 * CC-coverage dot shown next to a ticker in the Positions table.
 *  RED   = no open CC (none of the held shares are covered)
 *  AMBER = partially covered (open CC shares < shares held)
 *  GREEN = fully covered (open CC shares >= shares held)
 * "Covered shares" = Σ(open CC qty × 100) matched by ticker (linked or unlinked).
 */
function CoverageDot({ group }: { group: AggregatedPosition }) {
  const color =
    group.coverage === 'full'
      ? 'bg-[var(--color-green)]'
      : group.coverage === 'partial'
        ? 'bg-amber-500'
        : 'bg-[var(--color-red)]'
  const title =
    group.coverage === 'full'
      ? `Fully covered by open CCs (${group.coveredShares} of ${group.quantity} shares)`
      : group.coverage === 'partial'
        ? `Partially covered (${group.coveredShares} of ${group.quantity} shares)`
        : 'No open CC — shares uncovered'
  return (
    <span
      className={`inline-block w-2 h-2 rounded-full mr-1.5 align-middle ${color}`}
      title={title}
      role="img"
      aria-label={title}
    />
  )
}

const POSITION_COLUMNS = ['ticker', 'shares', 'costBasis', 'adjustedCost', 'totalCost', 'acquired', 'unrealizedPnL', 'linkedTrades'] as const
const POSITION_LABELS: Record<string, string> = {
  ticker: 'Ticker', shares: 'Shares', costBasis: 'Cost Basis',
  adjustedCost: 'Adj. Cost', totalCost: 'Total Cost', acquired: 'Acquired',
  unrealizedPnL: 'Unrealized P&L', linkedTrades: 'Linked',
}

interface PositionsSectionProps {
  positions: Position[]
  accountSlug: string
  trades: any[]
  prices: Record<string, { currentPrice: number }>
  onAddPosition: (position: any) => Promise<void>
  onUpdatePosition: (positionId: string, updates: any) => Promise<void>
  onDeletePosition: (positionId: string) => Promise<void>
  onSelectPosition?: (ticker: string) => void
  onLinkTrade?: (tradeId: string, positionId: string) => Promise<void>
  onUnlinkTrade?: (tradeId: string) => Promise<void>
}

export function PositionsSection({
  positions,
  accountSlug,
  trades,
  prices,
  onAddPosition,
  onUpdatePosition,
  onDeletePosition,
  onSelectPosition,
  onLinkTrade,
  onUnlinkTrade,
}: PositionsSectionProps) {
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingPosition, setEditingPosition] = useState<Position | null>(null)
  const [sellingGroup, setSellingGroup] = useState<AggregatedPosition | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [linkingPositionId, setLinkingPositionId] = useState<string | null>(null)
  const [selectedLinkIds, setSelectedLinkIds] = useState<Set<string>>(new Set())
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try { return localStorage.getItem(`tradingApp_posSection_${accountSlug}`) !== 'expanded' }
    catch { return true }
  })
  const [sortColumn, setSortColumn] = useState<string>('ticker')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const { columnOrder, dragHandlers } = useColumnOrder(`positions-${accountSlug}`, [...POSITION_COLUMNS])

  const toggleCollapse = () => {
    const next = !isCollapsed
    setIsCollapsed(next)
    localStorage.setItem(`tradingApp_posSection_${accountSlug}`, next ? 'collapsed' : 'expanded')
  }

  const handleSort = (col: string) => {
    if (sortColumn === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortColumn(col); setSortDir('asc') }
  }

  const openPositions = positions.filter(p => p.status === 'open')
  // Display-time aggregation (Option B): group all open lots for the same ticker
  // into one line. Supabase keeps the separate lot rows. See lib/positionAggregation.ts.
  const aggregated = aggregatePositionsByTicker(openPositions, trades)

  if (openPositions.length === 0 && !showAddForm) {
    return (
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-[var(--color-text-muted)] uppercase tracking-wide">
            Share Positions (0)
          </h2>
          <button
            onClick={() => setShowAddForm(true)}
            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent)]/80"
          >
            + Add Position
          </button>
        </div>
        <p className="text-sm text-[var(--color-text-muted)] italic">No open positions</p>
        {showAddForm && (
          <AddPositionForm
            accountSlug={accountSlug}
            onSubmit={async (pos) => { await onAddPosition(pos); setShowAddForm(false) }}
            onCancel={() => setShowAddForm(false)}
          />
        )}
      </div>
    )
  }

  const handleAdd = async (pos: any) => {
    await onAddPosition(pos)
    setShowAddForm(false)
  }

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={toggleCollapse}
          className="flex items-center gap-2 text-sm font-semibold text-[var(--color-text-muted)] uppercase tracking-wide hover:text-[var(--color-text-primary)] transition-colors"
        >
          <span className={`transition-transform ${isCollapsed ? '' : 'rotate-90'}`}>▶</span>
          Share Positions ({aggregated.length})
        </button>
        <button
          onClick={() => setShowAddForm(true)}
          className="px-3 py-1.5 text-xs font-medium rounded-lg bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent)]/80"
        >
          + Add Position
        </button>
      </div>

      {!isCollapsed && (
      <>
      <div className="border border-[var(--color-border)] rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[640px] lg:min-w-0">
          <thead className="bg-[var(--color-bg-tertiary)]">
            <tr>
              {columnOrder.map((col, index) => (
                <th
                  key={col}
                  className="px-3 py-2 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase cursor-pointer hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-tertiary)] select-none whitespace-nowrap"
                  onClick={() => handleSort(col)}
                  {...dragHandlers(index)}
                >
                  {POSITION_LABELS[col] || col} {sortColumn === col && (sortDir === 'asc' ? '↑' : '↓')}
                </th>
              ))}
              <th className="px-3 py-2 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {[...aggregated].sort((a, b) => {
              const dir = sortDir === 'asc' ? 1 : -1
              const priceA = prices[a.ticker]?.currentPrice || 0
              const priceB = prices[b.ticker]?.currentPrice || 0
              switch (sortColumn) {
                case 'ticker': return a.ticker.localeCompare(b.ticker) * dir
                case 'shares': return (a.quantity - b.quantity) * dir
                case 'costBasis': return (a.costBasis - b.costBasis) * dir
                case 'totalCost': return (a.totalCost - b.totalCost) * dir
                case 'acquired': return (a.acquisitionDate || '').localeCompare(b.acquisitionDate || '') * dir
                case 'unrealizedPnL': {
                  const pnlA = priceA > 0 ? (priceA - a.costBasis) * a.quantity : 0
                  const pnlB = priceB > 0 ? (priceB - b.costBasis) * b.quantity : 0
                  return (pnlA - pnlB) * dir
                }
                case 'linkedCCs':
                case 'linkedTrades': return (a.linkedTradeCount - b.linkedTradeCount) * dir
                case 'adjustedCost': return (a.adjustedCostBasis - b.adjustedCostBasis) * dir
                default: return 0
              }
            }).map(group => {
              const currentPrice = prices[group.ticker]?.currentPrice || 0
              const unrealizedPnL = currentPrice > 0 ? (currentPrice - group.costBasis) * group.quantity : null
              const hasLinked = group.linkedTradeCount > 0

              const renderCell = (col: string) => {
                switch (col) {
                  case 'ticker': return <td key={col} className="px-3 py-2 font-medium text-[var(--color-accent)]"><CoverageDot group={group} />{group.ticker}<EarningsBadge ticker={group.ticker} />{group.lots.length > 1 && <span className="ml-1 text-[10px] text-[var(--color-text-muted)] font-normal">({group.lots.length} lots)</span>}</td>
                  case 'shares': return <td key={col} className="px-3 py-2 font-mono">{group.quantity}</td>
                  case 'costBasis': return <td key={col} className="px-3 py-2 font-mono">${group.costBasis.toFixed(2)}</td>
                  case 'adjustedCost': return (
                    <td key={col} className={`px-3 py-2 font-mono ${hasLinked ? 'text-[var(--color-green)]' : 'text-[var(--color-text-muted)]'}`}>
                      {hasLinked ? `$${group.adjustedCostBasis.toFixed(2)}` : '—'}
                    </td>
                  )
                  case 'totalCost': return <td key={col} className="px-3 py-2 font-mono">${group.totalCost.toFixed(2)}</td>
                  case 'acquired': return <td key={col} className="px-3 py-2 text-[var(--color-text-secondary)]">{formatMDY(group.acquisitionDate) || '—'}</td>
                  case 'unrealizedPnL': return (
                    <td key={col} className={`px-3 py-2 font-mono ${
                      unrealizedPnL === null ? 'text-[var(--color-text-muted)]' :
                      unrealizedPnL >= 0 ? 'text-[var(--color-green)]' : 'text-[var(--color-red)]'
                    }`}>
                      {unrealizedPnL !== null ? `$${unrealizedPnL.toFixed(2)}` : '—'}
                    </td>
                  )
                  case 'linkedCCs':
                  case 'linkedTrades': return (
                    <td key={col} className="px-3 py-2 text-xs font-mono">
                      {hasLinked ? (
                        <span className="text-[var(--color-accent)]">{group.linkedTradeCount} trade{group.linkedTradeCount > 1 ? 's' : ''}</span>
                      ) : <span className="text-[var(--color-text-muted)]">—</span>}
                    </td>
                  )
                  default: return <td key={col} className="px-3 py-2">—</td>
                }
              }

              return (
                <tr
                  key={group.ticker}
                  className="border-t border-[var(--color-border)] hover:bg-[var(--color-bg-tertiary)]/50 cursor-pointer group"
                  onClick={() => {
                    if (onSelectPosition) onSelectPosition(group.ticker)
                    setExpandedId(expandedId === group.ticker ? null : group.ticker)
                  }}
                >
                  {columnOrder.map(col => renderCell(col))}
                  <td className="px-3 py-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); setSellingGroup(group) }}
                      className="px-2 py-1 text-xs rounded bg-green-100 text-green-700 hover:bg-green-200 opacity-0 group-hover:opacity-100 transition-opacity mr-1"
                      title="Sell shares (FIFO across lots)"
                    >
                      💰 Sell
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setExpandedId(expandedId === group.ticker ? null : group.ticker) }}
                      className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-700 hover:bg-blue-200 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Expand to edit/delete individual lots"
                    >
                      Lots
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Expanded aggregated-position summary (grouped by ticker) */}
      {expandedId && (() => {
        const group = aggregated.find(g => g.ticker === expandedId)
        if (!group) return null
        const lotIds = new Set(group.lots.map(l => l.id))
        // The lot a new link attaches to (oldest lot = first after FIFO sort).
        const primaryLot = group.lots[0]
        const sourceTradeIds = new Set(group.lots.map(l => l.sourceTradeId).filter(Boolean))
        const allLinkedTrades = trades
          .filter((t: any) => t.positionId && lotIds.has(t.positionId))
          // Chronological order: earliest completion date at the top, latest at the bottom.
          .sort((a: any, b: any) => completionDate(a).localeCompare(completionDate(b)))
        const totalPremium = group.totalLinkedPremium
        const adjCost = group.adjustedCostBasis
        // Unlinked trades for the same ticker (candidates for linking)
        const unlinkableTrades = trades.filter((t: any) =>
          t.ticker === group.ticker && !t.positionId && !sourceTradeIds.has(t.id)
        ).sort((a: any, b: any) => (a.expDate || a.buyDate || '').localeCompare(b.expDate || b.buyDate || ''))

        return (
          <div className="mt-2 border border-[var(--color-border)] rounded-lg p-4 bg-[var(--color-bg-tertiary)]/50">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-semibold text-[var(--color-text-muted)] uppercase">Position Summary — {group.ticker} ({group.quantity} shares, {group.lots.length} lot{group.lots.length > 1 ? 's' : ''})</h4>
              {onLinkTrade && unlinkableTrades.length > 0 && primaryLot && (
                <button
                  onClick={(e) => { e.stopPropagation(); setLinkingPositionId(linkingPositionId === group.ticker ? null : group.ticker) }}
                  className="px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-700 hover:bg-blue-200"
                >
                  + Link Trade
                </button>
              )}
            </div>

            {/* Individual lots (edit / delete per lot) */}
            <div className="mb-3">
              <p className="text-xs font-medium text-[var(--color-text-muted)] mb-1">Lots ({group.lots.length}):</p>
              <div className="space-y-0.5">
                {group.lots.map(lot => (
                  <div key={lot.id} className="flex items-center justify-between text-xs px-2 py-1 rounded bg-[var(--color-bg-secondary)]">
                    <span>
                      <span className="font-mono font-medium">{lot.quantity}</span> sh @ <span className="font-mono">${lot.costBasis.toFixed(2)}</span>
                      <span className="text-[var(--color-text-muted)] ml-1">acq {formatMDY((lot as any).assignedDate || lot.acquisitionDate)}</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <button onClick={(e) => { e.stopPropagation(); setEditingPosition(lot) }} className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 hover:bg-blue-200" title="Edit lot">✏️</button>
                      <button onClick={(e) => { e.stopPropagation(); onDeletePosition(lot.id) }} className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 hover:bg-red-200" title="Delete lot">🗑️</button>
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Link trade dropdown — multi-select (attaches to the oldest lot) */}
            {linkingPositionId === group.ticker && onLinkTrade && primaryLot && (
              <div className="mb-3 border border-blue-200 rounded-lg p-2 bg-blue-50">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs font-medium text-blue-700">Select trades to link:</p>
                  {selectedLinkIds.size > 0 && (
                    <button
                      onClick={async (e) => {
                        e.stopPropagation()
                        for (const id of selectedLinkIds) {
                          await onLinkTrade(id, primaryLot.id)
                        }
                        setSelectedLinkIds(new Set())
                        setLinkingPositionId(null)
                      }}
                      className="px-2 py-1 text-xs font-medium rounded bg-blue-600 text-white hover:bg-blue-700"
                    >
                      Link {selectedLinkIds.size} selected
                    </button>
                  )}
                </div>
                <div className="max-h-40 overflow-y-auto space-y-0.5">
                  {unlinkableTrades.map((t: any) => (
                    <label
                      key={t.id}
                      className="flex items-center gap-2 px-2 py-1 text-xs rounded hover:bg-blue-100 cursor-pointer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={selectedLinkIds.has(t.id)}
                        onChange={() => {
                          setSelectedLinkIds(prev => {
                            const next = new Set(prev)
                            if (next.has(t.id)) next.delete(t.id)
                            else next.add(t.id)
                            return next
                          })
                        }}
                        className="rounded"
                      />
                      <span className="flex-1">{t.action} ${t.strikePrice} — {t.expDate || t.buyDate}</span>
                      <span className={`font-mono ${(t.netPosition || 0) >= 0 ? 'text-green-700' : 'text-red-600'}`}>${(t.netPosition || 0).toFixed(2)}</span>
                    </label>
                  ))}
                </div>
                <div className="flex justify-between mt-2 pt-1 border-t border-blue-200">
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setSelectedLinkIds(new Set(unlinkableTrades.map((t: any) => t.id)))
                    }}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >Select All</button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setSelectedLinkIds(new Set()); setLinkingPositionId(null) }}
                    className="text-xs text-gray-500 hover:text-gray-700"
                  >Cancel</button>
                </div>
              </div>
            )}

            {/* Linked trades list */}
            {allLinkedTrades.length > 0 && (
              <div className="mb-3">
                <p className="text-xs font-medium text-[var(--color-text-muted)] mb-1">Linked Trades ({allLinkedTrades.length}):</p>
                <div className="space-y-0.5">
                  {allLinkedTrades.map((t: any) => {
                    const isAssignment = t.closeAction === 'Assigned'
                    return (
                    <div
                      key={t.id}
                      className={`flex items-center justify-between text-xs px-2 py-1 rounded ${
                        isAssignment
                          ? 'bg-amber-100 ring-1 ring-amber-400'
                          : 'bg-[var(--color-bg-secondary)]'
                      }`}
                    >
                      <span>
                        <span className="font-medium">{t.action}</span> ${t.strikePrice} exp {formatMDY(t.expDate)}
                        {isAssignment ? (
                          <span className="ml-1 font-semibold text-amber-700">(assignment)</span>
                        ) : (
                          <span className="text-[var(--color-text-muted)] ml-1">({t.status})</span>
                        )}
                      </span>
                      <span className="flex items-center gap-2">
                        <span className={`font-mono ${(t.netPosition || 0) >= 0 ? 'text-[var(--color-green)]' : 'text-[var(--color-red)]'}`}>${(t.netPosition || 0).toFixed(2)}</span>
                        {onUnlinkTrade && (
                          <button
                            onClick={async (e) => { e.stopPropagation(); await onUnlinkTrade(t.id) }}
                            className="text-red-500 hover:text-red-700"
                            title="Unlink trade"
                          >✕</button>
                        )}
                      </span>
                    </div>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Cost basis summary */}
            <div className="grid grid-cols-3 gap-4 text-sm border-t border-[var(--color-border)] pt-3">
              <div>
                <p className="text-[var(--color-text-muted)] text-xs">Blended Cost Basis</p>
                <p className="font-mono font-medium">${group.costBasis.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-[var(--color-text-muted)] text-xs">Total Premiums Collected</p>
                <p className="font-mono font-medium text-[var(--color-green)]">${totalPremium.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-[var(--color-text-muted)] text-xs">Adjusted Cost Basis</p>
                <p className={`font-mono font-bold ${adjCost < group.costBasis ? 'text-[var(--color-green)]' : ''}`}>${adjCost.toFixed(2)}</p>
              </div>
            </div>
          </div>
        )
      })()}
      </>
      )}

      {showAddForm && (
        <AddPositionForm
          accountSlug={accountSlug}
          onSubmit={handleAdd}
          onCancel={() => setShowAddForm(false)}
        />
      )}

      {editingPosition && (
        <EditPositionForm
          position={editingPosition}
          onSubmit={async (updates) => { await onUpdatePosition(editingPosition.id, updates); setEditingPosition(null) }}
          onCancel={() => setEditingPosition(null)}
        />
      )}

      {sellingGroup && (
        <SellPositionModal
          position={{
            // Synthetic position representing the whole ticker group for the modal UI.
            id: sellingGroup.ticker,
            accountSlug,
            ticker: sellingGroup.ticker,
            quantity: sellingGroup.quantity,
            costBasis: sellingGroup.costBasis,
            acquisitionDate: sellingGroup.acquisitionDate,
            sourceTradeId: '',
            status: 'open',
          }}
          onSubmit={async (soldPrice, sharesSold, closeDate) => {
            // FIFO: draw sold shares from the oldest lot first, spilling into later lots.
            // (lots are already FIFO-ordered: oldest acquisition first.)
            let remaining = sharesSold
            for (const lot of sellingGroup.lots) {
              if (remaining <= 0) break
              const take = Math.min(remaining, lot.quantity)
              const newQty = lot.quantity - take
              await onUpdatePosition(lot.id, {
                quantity: newQty,
                status: newQty <= 0 ? 'closed' : 'open',
                sold_price: soldPrice,
                sold_date: closeDate,
                shares_sold: take,
              })
              remaining -= take
            }
            setSellingGroup(null)
          }}
          onCancel={() => setSellingGroup(null)}
        />
      )}
    </div>
  )
}

// === Sell Position Modal ===
function SellPositionModal({ position, onSubmit, onCancel }: {
  position: Position
  onSubmit: (soldPrice: number, sharesSold: number, closeDate: string) => Promise<void>
  onCancel: () => void
}) {
  const [soldPrice, setSoldPrice] = useState('')
  const [sharesSold, setSharesSold] = useState(String(position.quantity))
  const [closeDate, setCloseDate] = useState(new Date().toISOString().split('T')[0])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const price = parseFloat(soldPrice)
    const shares = parseInt(sharesSold)

    if (!price || price <= 0) { setError('Sold price is required'); return }
    if (!shares || shares <= 0) { setError('Shares sold must be > 0'); return }
    if (shares > position.quantity) { setError(`Cannot sell more than ${position.quantity} shares`); return }

    setSubmitting(true)
    try {
      await onSubmit(price, shares, closeDate)
    } catch {
      setError('Failed to sell position')
      setSubmitting(false)
    }
  }

  const appreciation = soldPrice ? ((parseFloat(soldPrice) - position.costBasis) * parseInt(sharesSold || '0')).toFixed(2) : null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-6" onClick={onCancel}>
      <div className="bg-white rounded-2xl p-8 w-full max-w-sm shadow-2xl" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-bold text-[var(--color-text-primary)]">Sell Shares</h2>
        <p className="text-sm text-[var(--color-text-muted)] mt-1">
          {position.ticker} — {position.quantity} shares @ ${position.costBasis.toFixed(2)}/share
        </p>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-[var(--color-text-muted)]">Sold Price Per Share</label>
            <input
              type="number"
              step="0.01"
              value={soldPrice}
              onChange={e => setSoldPrice(e.target.value)}
              placeholder="e.g. 95.50"
              className="px-3 py-2 border border-[var(--color-border)] rounded-lg bg-white"
              autoFocus
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-[var(--color-text-muted)]">Shares Sold</label>
            <input
              type="number"
              step="1"
              value={sharesSold}
              onChange={e => setSharesSold(e.target.value)}
              className="px-3 py-2 border border-[var(--color-border)] rounded-lg bg-white"
            />
            <span className="text-xs text-[var(--color-text-muted)]">Position has {position.quantity} shares</span>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-[var(--color-text-muted)]">Close Date</label>
            <input
              type="date"
              value={closeDate}
              onChange={e => setCloseDate(e.target.value)}
              className="px-3 py-2 border border-[var(--color-border)] rounded-lg bg-white"
            />
          </div>

          {appreciation && parseFloat(soldPrice) > 0 && (
            <div className={`px-3 py-2 rounded-lg border text-sm ${
              parseFloat(appreciation) >= 0
                ? 'bg-green-50 border-green-200 text-green-800'
                : 'bg-red-50 border-red-200 text-red-800'
            }`}>
              Share Appreciation: <span className="font-mono font-bold">${appreciation}</span>
            </div>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex justify-end gap-3 pt-4 border-t border-[var(--color-border)]">
            <button type="button" onClick={onCancel} className="px-4 py-2 rounded-lg border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)]">Cancel</button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 rounded-lg bg-green-600 text-white font-medium hover:bg-green-700 disabled:opacity-50"
            >
              {submitting ? 'Selling...' : 'Sell Shares'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
